// Message Listener Hook
// VAN-021: Hook for receiving messages from parent window when in iframe mode

import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/core/store";
import {
  isValidParentMessage,
  ParentToIframeMessage,
  ThemeChangeData,
} from "@/types/iframe-messages";
import { setParentReady, setTheme, setCustomColors } from "@/core/app/reducer";
import { PaletteOptions } from "@mui/material/styles";

interface MessageListenerOptions {
  onThemeChange?: (themeData: ThemeChangeData) => void;
  onParentReady?: () => void;
  enableLogging?: boolean;
  trustedOrigins?: string[]; // Array of trusted origin patterns
}

/**
 * Hook for listening to messages from parent window
 * Handles theme updates, parent ready status, and custom message handling
 */
export const useMessageListener = (options: MessageListenerOptions = {}) => {
  const {
    onThemeChange,
    onParentReady,
    enableLogging = true,
    trustedOrigins = ["*"], // Default to allow all origins (can be restricted)
  } = options;

  const dispatch = useDispatch();
  const { isIframe } = useSelector((state: RootState) => state.app);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(
    null
  );

  // Check if origin is trusted
  const isTrustedOrigin = useCallback(
    (origin: string): boolean => {
      if (trustedOrigins.includes("*")) return true;
      return trustedOrigins.some((pattern) => {
        if (pattern.includes("*")) {
          // Simple wildcard matching
          const regex = new RegExp(pattern.replace(/\*/g, ".*"));
          return regex.test(origin);
        }
        return pattern === origin;
      });
    },
    [trustedOrigins]
  );

  // Handle incoming message from parent
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Security check: verify origin if not in development
      if (
        process.env.NODE_ENV === "production" &&
        !isTrustedOrigin(event.origin)
      ) {
        if (enableLogging) {
          console.warn("useMessageListener: Untrusted origin:", event.origin);
        }
        return;
      }

      // Validate message structure
      if (!isValidParentMessage(event.data)) {
        if (enableLogging) {
          console.log(
            "useMessageListener: Invalid parent message:",
            event.data
          );
        }
        return;
      }

      const message: ParentToIframeMessage = event.data;

      if (enableLogging) {
        console.log("useMessageListener: Received parent message:", message);
      }

      // Handle different message types
      switch (message.type) {
        case "PARENT_READY":
          dispatch(setParentReady(true));
          if (onParentReady) {
            onParentReady();
          }
          break;

        case "SET_THEME":
          if (message.payload) {
            const themeData = message.payload as ThemeChangeData;

            // Update Redux state
            dispatch(setTheme(themeData.theme));
            if (themeData.colors) {
              dispatch(
                setCustomColors(themeData.colors as unknown as PaletteOptions)
              );
            }

            // Call custom handler if provided
            if (onThemeChange) {
              onThemeChange(themeData);
            }
          }
          break;

        default:
          if (enableLogging) {
            console.log(
              "useMessageListener: Unknown message type:",
              message.type
            );
          }
      }
    },
    [dispatch, isTrustedOrigin, enableLogging, onThemeChange, onParentReady]
  );

  // Set up message listener
  useEffect(() => {
    if (!isIframe) {
      if (enableLogging) {
        console.log(
          "useMessageListener: Not in iframe mode, listener not activated"
        );
      }
      return;
    }

    // Store reference to handler for cleanup
    messageHandlerRef.current = handleMessage;

    // Add event listener
    window.addEventListener("message", handleMessage);

    if (enableLogging) {
      console.log("useMessageListener: Message listener activated");
    }

    // Cleanup function
    return () => {
      window.removeEventListener("message", handleMessage);
      messageHandlerRef.current = null;
      if (enableLogging) {
        console.log("useMessageListener: Message listener deactivated");
      }
    };
  }, [isIframe, handleMessage, enableLogging]);

  // Manually remove listener (useful for component unmount)
  const removeListener = useCallback(() => {
    if (messageHandlerRef.current) {
      window.removeEventListener("message", messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
  }, []);

  return {
    // State
    isListening: isIframe && messageHandlerRef.current !== null,

    // Controls
    removeListener,

    // Utilities
    isTrustedOrigin,
  };
};
